import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, DollarSign, Briefcase, CheckCircle } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const jobs = [
    {
      id: 1,
      title: "OPC Marketing Representative",
      department: "Marketing",
      location: "Orlando, FL",
      type: "Full-time",
      salary: "$40,000 - $80,000+ (Commission)",
      description: "Join our OPC marketing team and help vacationers discover amazing resort experiences while earning great commission.",
      requirements: [
        "Must be outgoing and assertive",
        "Excellent face-to-face customer service skills",
        "Ability to handle cash and tickets accurately",
        "Flexible schedule including weekends and holidays",
        "Passion for helping clients get better deals"
      ]
    },
    {
      id: 2,
      title: "Digital Marketing Specialist",
      department: "Digital",
      location: "Remote / Orlando, FL",
      type: "Full-time",
      salary: "$55,000 - $75,000",
      description: "Manage and optimize digital advertising campaigns across Google, social media, and CTV platforms.",
      requirements: [
        "3+ years digital marketing experience",
        "Proficiency in Google Ads and Meta Ads",
        "Strong analytical skills",
        "Experience with marketing automation",
        "Excellent communication skills"
      ]
    },
    {
      id: 3,
      title: "Call Center Representative",
      department: "Sales",
      location: "Orlando, FL",
      type: "Full-time",
      salary: "$35,000 - $55,000+ (Commission)",
      description: "Set appointments and conduct outreach calls for our resort partners. Great earning potential with commission.",
      requirements: [
        "Strong phone communication skills",
        "Sales or customer service experience preferred",
        "Goal-oriented mindset",
        "Ability to handle objections",
        "Professional demeanor"
      ]
    },
    {
      id: 4,
      title: "Marketing Manager",
      department: "Leadership",
      location: "Orlando, FL",
      type: "Full-time",
      salary: "$70,000 - $100,000",
      description: "Lead a team of marketers and oversee campaign strategy for our timeshare and resort clients.",
      requirements: [
        "5+ years marketing management experience",
        "Experience in hospitality or timeshare industry",
        "Strong leadership and team building skills",
        "Data-driven decision making",
        "Excellent project management abilities"
      ]
    }
  ];

  const benefits = [
    "Competitive base salary + commission",
    "Health, dental, and vision insurance",
    "Paid time off and holidays",
    "Career growth opportunities",
    "Team building events",
    "Performance bonuses"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.experience || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/careers`, {
        ...formData,
        position: selectedJob?.title || formData.position
      });
      if (response.data.success) {
        toast.success('Application submitted! We\'ll be in touch soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          message: ''
        });
        setSelectedJob(null);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Join Our Team
            </span>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mb-6">
              Build Your Career at Prestigious
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              We're looking for passionate, driven individuals to join our team of marketing 
              trailblazers. If you have the hustle mentality and want to make an impact, 
              we want to hear from you.
            </p>
            <Link to="#openings">
              <Button 
                size="lg" 
                className="bg-[#1E3A8A] hover:bg-[#1e40af] text-white px-8"
                data-testid="view-openings-btn"
              >
                View Open Positions
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
                Why Join Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                More Than Just a Job
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                At Prestigious Marketing Inc, we believe in the 'Hustle' mentality - working 
                hard, playing hard, and achieving extraordinary results together. When you 
                join our team, you become part of a family that's dedicated to success.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#0D9488]" />
                    <span className="text-slate-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#0F172A] rounded-2xl p-8 lg:p-12 text-white">
              <h3 className="text-2xl font-bold mb-6">Our Culture</h3>
              <div className="space-y-6">
                {[
                  { title: "Hustle Mentality", desc: "We never quit until we achieve the extraordinary." },
                  { title: "Team First", desc: "Success is a team sport. We win together." },
                  { title: "Growth Focused", desc: "We invest in your professional development." },
                  { title: "Results Driven", desc: "Performance is recognized and rewarded." }
                ].map((item, idx) => (
                  <div key={idx}>
                    <h4 className="font-bold text-[#EA580C] mb-1">{item.title}</h4>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Listings Section */}
      <section id="openings" className="section-padding bg-slate-50" data-testid="job-listings">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Open Positions
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Current Opportunities
            </h2>
          </div>

          <div className="space-y-4">
            {jobs.map((job) => (
              <Card 
                key={job.id} 
                className="border-slate-200 bg-white job-card cursor-pointer"
                data-testid={`job-card-${job.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <span className="inline-block px-3 py-1 bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs font-bold uppercase tracking-wider rounded-full mb-2">
                        {job.department}
                      </span>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{job.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} /> {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} /> {job.type}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign size={14} /> {job.salary}
                        </span>
                      </div>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          className="bg-[#1E3A8A] hover:bg-[#1e40af]"
                          onClick={() => setSelectedJob(job)}
                          data-testid={`apply-btn-${job.id}`}
                        >
                          Apply Now
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-2xl">{job.title}</DialogTitle>
                          <DialogDescription className="text-slate-500">
                            {job.department} • {job.location} • {job.type}
                          </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-6 mt-4">
                          <div>
                            <h4 className="font-bold text-slate-900 mb-2">About the Role</h4>
                            <p className="text-slate-600 text-sm">{job.description}</p>
                          </div>
                          
                          <div>
                            <h4 className="font-bold text-slate-900 mb-2">Requirements</h4>
                            <ul className="space-y-2">
                              {job.requirements.map((req, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                                  <CheckCircle className="w-4 h-4 text-[#0D9488] mt-0.5 flex-shrink-0" />
                                  {req}
                                </li>
                              ))}
                            </ul>
                          </div>

                          <form onSubmit={handleSubmit} className="space-y-4 border-t pt-6" data-testid="career-form">
                            <h4 className="font-bold text-slate-900">Apply for this Position</h4>
                            
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="name">Full Name *</Label>
                                <Input
                                  id="name"
                                  name="name"
                                  value={formData.name}
                                  onChange={handleChange}
                                  className="mt-1"
                                  data-testid="career-name-input"
                                />
                              </div>
                              <div>
                                <Label htmlFor="email">Email *</Label>
                                <Input
                                  id="email"
                                  name="email"
                                  type="email"
                                  value={formData.email}
                                  onChange={handleChange}
                                  className="mt-1"
                                  data-testid="career-email-input"
                                />
                              </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="phone">Phone *</Label>
                                <Input
                                  id="phone"
                                  name="phone"
                                  value={formData.phone}
                                  onChange={handleChange}
                                  className="mt-1"
                                  data-testid="career-phone-input"
                                />
                              </div>
                              <div>
                                <Label htmlFor="experience">Years of Experience *</Label>
                                <Input
                                  id="experience"
                                  name="experience"
                                  value={formData.experience}
                                  onChange={handleChange}
                                  placeholder="e.g., 3 years"
                                  className="mt-1"
                                  data-testid="career-experience-input"
                                />
                              </div>
                            </div>

                            <div>
                              <Label htmlFor="message">Why should we hire you? *</Label>
                              <Textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                className="mt-1 min-h-[100px]"
                                data-testid="career-message-input"
                              />
                            </div>

                            <Button
                              type="submit"
                              className="w-full bg-[#EA580C] hover:bg-[#c2410c]"
                              disabled={isSubmitting}
                              data-testid="career-submit-btn"
                            >
                              {isSubmitting ? 'Submitting...' : 'Submit Application'}
                            </Button>
                          </form>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
            Don't See Your Perfect Role?
          </h2>
          <p className="text-slate-600 mb-10 max-w-2xl mx-auto">
            We're always looking for talented individuals. Send us your resume and 
            tell us how you can contribute to our team.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-[#1E3A8A] hover:bg-[#1e40af]"
              data-testid="general-application-btn"
            >
              Submit General Application
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Careers;
